import React, { memo } from 'react';
import {
  CheckIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
} from '@heroicons/react/outline';
import { StoreValues } from 'types/store';

export enum BILLING_PROVIDER {
  SHOPIFY_BILLING = 'SHOPIFY_BILLING',
  STRIPE_BILLING = 'STRIPE_BILLING',
}

interface Step4Props {
  formData: {
    billingProvider?: BILLING_PROVIDER;
    useCustomApp: boolean;
    stripeBillingEmail?: string;
    shopifyUrl: string;
  };
  storeProperties?: StoreValues;
  storeId?: string;
  user?: {
    email?: string;
  };
  billingConnected: boolean;
  setStorePlan: (opts: { variables: { storeId: string } }) => void;
  setupStripeConnection: (opts: {
    variables: { store: string; email?: string };
  }) => void;
  handleStripeBillingEmail: (e: React.ChangeEvent<HTMLInputElement>) => void;
  saveOnboarding: (payload?: any) => void;
  handleBillingProvider: (provider: BILLING_PROVIDER) => void;
}

const Step4: React.FC<Step4Props> = ({
  formData,
  storeProperties,
  storeId,
  user,
  billingConnected,
  setStorePlan,
  setupStripeConnection,
  handleStripeBillingEmail,
  saveOnboarding,
  handleBillingProvider,
}) => {
  return (
    <div>
      <div className="flex justify-start gap-2 mb-5 mx-auto w-[85%]">
        <div
          className="mr-5 w-[200px]"
          dangerouslySetInnerHTML={{
            __html: `<div style="position: relative; box-sizing: content-box; width: 100%; aspect-ratio: 2.1201353637901863;"><iframe src="https://guides.shipaid.com/embed/cmcwg5r0l1gfz9empn5rrj64z?embed_v=2" loading="lazy" title="Choose How You’d Like to Be Billed" allow="clipboard-write" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 200px; height: 150px"></iframe></div>`,
          }}
        />
        <div className="text-left">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Choose How You’d Like to Be Billed
          </h2>
          <p className="text-gray-600 mb-8">
            <strong>
              No monthly fees, no onboarding costs, no commitment.
            </strong>{' '}
            Our pricing is <strong>100% performance-based</strong>, only pay
            when you earn. (
            <a
              href="https://www.shipaid.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              learn how it works
            </a>
            )
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div
          onClick={() => {
            if (!formData.useCustomApp) {
              handleBillingProvider(BILLING_PROVIDER.SHOPIFY_BILLING);
              saveOnboarding({
                billingProvider: BILLING_PROVIDER.SHOPIFY_BILLING,
              });
            }
          }}
          className={`cursor-pointer p-6 border-2 rounded-lg transition-all duration-200 ${
            formData.billingProvider === BILLING_PROVIDER.SHOPIFY_BILLING
              ? 'border-[#2563eb] bg-blue-50 ring-2 ring-blue-100'
              : 'border-gray-200 hover:border-[#2563eb] hover:bg-blue-50'
          } ${formData.useCustomApp ? 'opacity-[0.5]' : ''}`}
        >
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/shopify-icon.png"
                alt="shopify-billing"
                className="w-10 h-10"
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Shopify Billing
            </h3>
            <p className="text-gray-600 mb-4">
              Billing through your Shopify admin. Fast, seamless, and
              integrated.
            </p>
            <div className="text-sm text-green-600 font-medium">
              ✓ Integrated with Shopify
            </div>
            <div className="text-sm text-green-600 font-medium">
              ✓ Unified billing dashboard
            </div>
            <div className="text-sm text-green-600 font-medium">
              ✓ Automatic currency conversion
            </div>
          </div>
        </div>

        <div
          onClick={() => {
            if (formData.useCustomApp) {
              handleBillingProvider(BILLING_PROVIDER.STRIPE_BILLING);
              saveOnboarding({
                billingProvider: BILLING_PROVIDER.STRIPE_BILLING,
              });
            }
          }}
          className={`cursor-pointer p-6 border-2 rounded-lg transition-all duration-200 ${
            formData.billingProvider === BILLING_PROVIDER.STRIPE_BILLING
              ? 'border-[#2563eb] bg-blue-50 ring-2 ring-blue-100'
              : 'border-gray-200 hover:border-[#2563eb] hover:bg-blue-50'
          } ${formData.useCustomApp ? '' : 'opacity-[0.5]'}`}
        >
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img
                src="/stripe-icon.png"
                alt="stripe-billing"
                className="w-10 h-10"
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Stripe Billing
            </h3>
            <p className="text-gray-600 mb-4">
              More billing control with analytics, flexibility, and global
              payment options.
            </p>
            <div className="text-sm text-blue-600 font-medium">
              ✓ Advanced analytics
            </div>
            <div className="text-sm text-blue-600 font-medium">
              ✓ Multiple payment methods
            </div>
            <div className="text-sm text-blue-600 font-medium">
              ✓ Detailed reporting
            </div>
          </div>
        </div>
      </div>

      {formData.billingProvider && (
        <div className="text-center">
          {formData.billingProvider === BILLING_PROVIDER.SHOPIFY_BILLING && (
            <div className="bg-green-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-green-800 mb-4">
                Shopify Billing Selected
              </h6>
              <p className="text-green-700 mb-6">
                You’ll now be redirected to Shopify to authorize your billing.
                This process is secure and fully managed by Shopify.
              </p>
              <button
                onClick={() => {
                  if (storeProperties?.plan?.chargeUrl) {
                    window.location.href = storeProperties.plan.chargeUrl;
                  } else if (storeId) {
                    setStorePlan({ variables: { storeId } });
                  }
                  saveOnboarding();
                }}
                disabled={billingConnected}
                className={`inline-flex items-center px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  billingConnected
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {billingConnected ? (
                  <>
                    <CheckIcon className="mr-2 w-6 h-6" />
                    Billing Authorized
                  </>
                ) : (
                  <>
                    <ShieldCheckIcon className="mr-2 w-6 h-6" />
                    Authorize in Shopify
                  </>
                )}
              </button>
            </div>
          )}

          {formData.billingProvider === BILLING_PROVIDER.STRIPE_BILLING && (
            <div className="bg-blue-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-blue-800 mb-4">
                Stripe Billing Selected
              </h6>
              <p className="text-blue-700 mb-6">
                You’re about to securely set up your billing through Stripe, a
                PCI-compliant platform trusted by millions of businesses.
              </p>
              <p className="text-blue-700 mb-6">
                Please enter the email address where you’d like to receive
                billing receipts, subscription updates, and payment
                confirmations.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Notification Email
                </label>
                <input
                  type="email"
                  value={formData.stripeBillingEmail || ''}
                  onChange={handleStripeBillingEmail}
                  placeholder="Eg: your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
                />
              </div>
              <p className="text-sm my-2 text-gray-500">
                This is not used for logging into Stripe. It’s only for billing
                communications.
              </p>
              <button
                onClick={() => {
                  setupStripeConnection({
                    variables: {
                      store: formData.shopifyUrl,
                      email: user?.email,
                    },
                  });
                  saveOnboarding();
                }}
                disabled={
                  billingConnected ||
                  !formData.billingProvider ||
                  !formData.stripeBillingEmail
                }
                className={`inline-flex items-center px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  billingConnected
                    ? 'bg-blue-600 text-white cursor-default'
                    : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {billingConnected ? (
                  <>
                    <CheckIcon className="mr-2 w-6 h-6" />
                    Stripe Connected
                  </>
                ) : (
                  <>
                    <PlusCircleIcon className="mr-2 w-6 h-6" />
                    Authorize in Stripe
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(Step4);
